import {
  BacktestingScoreBreakdown,
  BacktestingTimeframe,
  DisplayCurrency,
  FiltersData,
  FilterType,
  LanguageNameSpaces,
  MarketplaceSubGroup,
  ScoreValue,
  SortingOrder,
  SubGroupReviewRejectionReason,
} from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";
import {
  REJECTION_REASON_TO_MESSAGE,
  RISK_TYPE_TO_PERCENT_RANGE,
  SCORE_SECTION_TO_KEYS,
} from "@shared/features/newMarketplace/constants";
import { sortByNumberProperty } from "@shared/utils/general";
import {
  filterByExchanges,
  filterByPrice,
  testAvgRatingFilter,
  testRangeFilter,
  testTagsFilter,
} from "@shared/features/filters/utils";
import { RiskType, ScoreSection } from "@shared/features/newMarketplace/types";
import { TranslationKeys } from "@shared/common/i18n/types";
import { joinWords } from "@shared/common/i18n/utils";

export const getNetPnlChange = (
  group: MarketplaceSubGroup | undefined,
  timeframe: BacktestingTimeframe,
  displayCurrency: DisplayCurrency
) => {
  const backtesting = group?.timeframe_to_backtesting_run_data?.[timeframe];
  const realisedPnl = backtesting?.realised_pnl?.[displayCurrency] || 0;
  const unrealisedPnl = backtesting?.unrealised_pnl?.[displayCurrency] || 0;
  const netPnl = realisedPnl + unrealisedPnl;
  const initialFunds = backtesting?.initial_portfolio_size?.[displayCurrency];
  return initialFunds
    ? toFixed((netPnl / initialFunds) * 100, PERCENT_PRECISION)
    : 0;
};

export const getSumScoreByKey = (
  scoreBreakdown: BacktestingScoreBreakdown | undefined,
  scoreBreakdownKey: keyof BacktestingScoreBreakdown,
  sumKey: keyof ScoreValue
) => {
  const sum = Object.values(scoreBreakdown?.[scoreBreakdownKey] || {}).reduce(
    (sum, value) => {
      return sum + value[sumKey];
    },
    0
  );
  if (sumKey === "value") return sum;
  return sum * 100;
};

export const getSumValues = (
  scoreBreakdown: BacktestingScoreBreakdown | undefined,
  scoreBreakdownKey: keyof BacktestingScoreBreakdown
) => {
  let sumValues = {};
  ["value", "weighted_score", "weight"].forEach((key) => {
    sumValues = {
      ...sumValues,
      [key]: getSumScoreByKey(
        scoreBreakdown,
        scoreBreakdownKey,
        key as keyof ScoreValue
      ),
    };
  });
  return sumValues;
};

export const roundScoreValues = (
  values: number[],
  total = Math.round(values.reduce((sum, value) => sum + value, 0))
): number[] => {
  const roundedValues = values.map(Math.floor);
  const remainder =
    total - roundedValues.reduce((sum, value) => sum + value, 0);
  values
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((first, second) => second.fraction - first.fraction)
    .slice(0, remainder)
    .forEach(({ index }) => {
      roundedValues[index] += 1;
    });
  return roundedValues;
};

export const getRiskValue = (scoreBreakdown?: BacktestingScoreBreakdown) => {
  const weightedScoreSum = SCORE_SECTION_TO_KEYS[ScoreSection.RISK].reduce(
    (sum, key) => {
      return sum + getSumScoreByKey(scoreBreakdown, key, "weighted_score");
    },
    0
  );
  const weightSum = SCORE_SECTION_TO_KEYS[ScoreSection.RISK].reduce(
    (sum, key) => {
      return sum + getSumScoreByKey(scoreBreakdown, key, "weight");
    },
    0
  );

  return (1 - weightedScoreSum / weightSum) * 100;
};

export const getIsHighRisk = (scoreBreakdown?: BacktestingScoreBreakdown) => {
  return (
    getRiskValue(scoreBreakdown) > RISK_TYPE_TO_PERCENT_RANGE[RiskType.HIGH][0]
  );
};

export const getPnlSortCallback = (
  timeframe: BacktestingTimeframe,
  displayCurrency: DisplayCurrency,
  sortDirection?: SortingOrder
) => {
  return (a: MarketplaceSubGroup, b: MarketplaceSubGroup) => {
    const aValue = getNetPnlChange(a, timeframe, displayCurrency);
    const bValue = getNetPnlChange(b, timeframe, displayCurrency);
    return sortByNumberProperty(aValue, bValue, undefined, sortDirection);
  };
};

export const getRiskSortCallback = (sortDirection?: SortingOrder) => {
  return (a: MarketplaceSubGroup, b: MarketplaceSubGroup) => {
    const aValue = getRiskValue(a.backtesting_score_breakdown);
    const bValue = getRiskValue(b.backtesting_score_breakdown);
    return sortByNumberProperty(aValue, bValue, undefined, sortDirection);
  };
};

export const filterMarketplaceGroups = (
  groups: MarketplaceSubGroup[],
  filters: FiltersData
) => {
  let filteredGroups = filterByPrice(
    filterByExchanges(groups, filters[FilterType.EXCHANGES]?.value || []),
    filters[FilterType.PRICE_RANGE]?.value
  );
  return filteredGroups.filter((group) => {
    return (
      testRangeFilter(
        filters[FilterType.AVG_NUM_SIGNALS]?.value,
        group.average_weekly_signals
      ) &&
      testAvgRatingFilter(
        filters[FilterType.AVG_RATING]?.value,
        group.avg_stars
      ) &&
      testTagsFilter(filters[FilterType.TAGS]?.value, group)
    );
  });
};

export const getRoundedAvgRating = (subgroupAvgStars: number) =>
  Math.round(subgroupAvgStars * 2) / 2;

export const getRejectedReviewMessageContent = (
  rejectionReason = SubGroupReviewRejectionReason.OTHER,
  t: (key: TranslationKeys<LanguageNameSpaces.MARKETPLACE>) => string
) => {
  return joinWords([
    t("reviews.rejected_review_prefix"),
    t(REJECTION_REASON_TO_MESSAGE[rejectionReason]) ??
      t(REJECTION_REASON_TO_MESSAGE[SubGroupReviewRejectionReason.OTHER]),
  ]);
};
